import { useState, useEffect } from "react";
import { DesktopStepWizard } from "./components/desktop-step-wizard";
import { ResultCard } from "./components/result-card";
import { OptimizedResultCard } from "./components/optimized-result-card";
import { CollapsedSummaryCard } from "./components/collapsed-summary-card";
import { MobileWizard } from "./components/mobile-wizard";
import { EmailValueCard } from "./components/email-value-card";
import { ValuePreviewPanel } from "./components/value-preview-panel";
import { SalaryCheckRequestModel } from "../../types/SalaryCheckRequest";
import { postSalaryCalculation } from "../../utils/api";
import { getCookie } from "../../utils/cookieService";
import { SalaryData } from "../../types/SalaryData";
import { MarketData } from "../../types/MarketData";
import Analytics from "../../utils/analytics";
// ...existing code...


export default function SalaryCheck() {
  const [salaryData, setSalaryData] = useState<SalaryData>({
    jobTitle: "",
    jobTitleId: 0,
    salary: 0,
    name: "",
    created: new Date(),
    experience: null,
    education: "",
    region: "",
    age: "",    
    ageGroup: "",
    jobSector: "",
    managementExperience: null,
    situation: "",    
    situationId: 0,
    sector: "",
    responsibility: "",
    purpose: "",
  });

  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isInputCollapsed, setIsInputCollapsed] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [calculationData, setCalculationData] = useState<SalaryCheckRequestModel | null>(null);
  const [contentSourceId, setContentSourceId] = useState<number>(0);


  const handleSalaryChange = (data: Partial<SalaryData>) => {
    const newData = { ...salaryData, ...data };
    setSalaryData(newData);

    // Don't auto-update results - only update when user clicks "Se min løncheck"
  };

  const handleJobTitleChange = (jobTitle: string) => {
    handleSalaryChange({ jobTitle });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const parseSalary = (value: unknown): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const normalized = value.replace(/[^0-9.,\-]/g, "").replace(/,/g, ".");
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };

  const parseRangeValue = (label: string): number => {
    const match = label.match(/(\d+\.?\d*)/g);
    if (!match) return 0;
    const last = match[match.length - 1];
    const value = Number(last.replace(/\./g, ""));
    return Number.isFinite(value) ? value * (label.toLowerCase().includes("k") ? 1000 : 1) : 0;
  };

  const mapToMarketData = (data: any): MarketData | null => {
    const calc = data;
    const average = parseSalary(calc.salaryAvg ?? data.salaryAvg ?? 0);
    const sampleSize = parseSalary(calc.numberOfSamples ?? data.numberOfSamples ?? 0);

    let rangeMin = 0;
    let rangeMax = 0;

    if (Array.isArray(calc.graphResult) && calc.graphResult.length > 0) {
      const firstLabel = String(calc.graphResult[0].xAxis ?? "");
      const lastLabel = String(calc.graphResult[calc.graphResult.length - 1].xAxis ?? "");
      rangeMin = parseRangeValue(firstLabel);
      rangeMax = parseRangeValue(lastLabel);
    }

    if (rangeMin === 0 && rangeMax === 0 && average > 0) {
      rangeMin = Math.round(average * 0.8);
      rangeMax = Math.round(average * 1.2);
    }

    const graphResult = Array.isArray(calc.graphResult)
      ? calc.graphResult.map((item: any) => ({
          xAxis: String(item.xAxis ?? ""),
          yAxis: parseSalary(item.yAxis ?? item.y ?? 0),
        }))
      : [];

    if (average > 0 && sampleSize > 0) {
      return { average, rangeMin, rangeMax, sampleSize, graphResult };
    }

    return null;
  };

  const handleCalculate = async (data: SalaryCheckRequestModel) => {
    setCalculationData(data);
    setIsCalculating(true);
    setHasCalculated(true);
    setIsInputCollapsed(true);

    try {
      const response = await postSalaryCalculation(data);
      if (response) {
        setMarketData(mapToMarketData(response.data));
      }
      setIsCalculating(false);
      setTimeout(() => {
        const el = document.getElementById("result-anchor");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      console.error("Løn tjek fejlede. Prøv igen");
      setIsCalculating(false);
    }
  };

  const form = document.querySelector(`[data-component='salary-check']`) as HTMLElement;
  const url = form?.dataset.url ?? "";
  const contentId = form?.dataset.contentid ?? "";

  useEffect(() => {
  setContentSourceId(contentId ? Number(contentId) : 0);
  }, []);

  const handleLead = async (name: string, email: string) => {
    setIsCalculating(true);
    const cookie = getCookie("__exponea_etc__");
    const lead: SalaryCheckRequestModel = {
      name: name,
      email: email,
      contentSourceId: contentId ? Number(contentId) : 0,
      situationId: calculationData?.situationId ?? 0,
      jobTitleId: calculationData?.jobTitleId ?? 0,
      jobTitle: calculationData?.jobTitle ?? "",
      salary: calculationData?.salary ?? 0,
      educationId: calculationData?.educationId ?? null,
      jobExperience: calculationData?.jobExperience ?? null,
      regionId: calculationData?.regionId ?? null,
      age: null,
      ageGroup: calculationData?.ageGroup ?? "",
      jobSector: calculationData?.jobSector ?? "",
      managementExperience: calculationData?.managementExperience ?? null,
      cookie: cookie ?? "",
      educationLevel: ""
    };

    try {
      await postSalaryCalculation(lead).then((res) => {
        if (res.status === 200) {
          //tracking lead
          let contentType = "submitted";
          if (salaryData.region) {
            contentType += `_${salaryData.region}`
          } 
          if(salaryData.sector) {
            contentType += `_${salaryData.sector}`
          }
          if(salaryData.experience) {
            contentType += `_${salaryData.experience}`
          }
          Analytics.getInstance().trackSalaryCheckWithValue(contentType, `${salaryData.salary}`)

          window.location.href = `${url}?infoKey=${res.data.Infokey}`;
          setIsCalculating(false);
        } else {
          console.error("Der opstod en fejl. Prøv igen");
        }
      });
    } catch (err) {
      console.error("Der opstod en fejl. Prøv igen");
    }
  };

  const hasResult = salaryData.jobTitle && salaryData.salary > 0 && marketData && hasCalculated;

  // Responsive handling
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 750 : true);

  const updateDeviceSize = () => {
    setIsDesktop(window.innerWidth >= 750);
  };

  useEffect(() => {
    updateDeviceSize();
    window.addEventListener("resize", updateDeviceSize);
    return () => window.removeEventListener("resize", updateDeviceSize);
  }, []);

  return (
    <div className="" style={{ background: "#F7F8FA" }}>
      {/* ==================== MOBILE EXPERIENCE ==================== */}
      {!isDesktop && (
        <div className="px-4 pb-24 pt-5">
          <div className="mt-5 space-y-3">
            {/* Phase 1: Guided wizard - only show when not collapsed */}
            {!isInputCollapsed && <MobileWizard salaryData={salaryData} onDataChange={handleSalaryChange} onCalculate={handleCalculate} isCollapsed={false} onToggleCollapse={() => setIsInputCollapsed(false)} resetTrigger={resetTrigger} contentSourceId={contentSourceId} />}

            {/* Phase 1 result: Free løncheck */}
            <div id="result-anchor" />
            {hasCalculated && (
              <ResultCard
                salaryData={salaryData}
                marketData={marketData}
                isCalculating={isCalculating}
                onAdjustData={() => {
                  setIsInputCollapsed(false);
                  setResetTrigger((prev) => prev + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}

            {hasResult && (
              <>
                {/* Email signup */}
                <EmailValueCard onEmailSent={handleLead} />
              </>
            )}

            {isCalculating && (
              <div className="spinnerWrapper">
                <div className="spinner">
                    <img className="spinnerImg" src="/assets/images/loading.svg" alt="loader" />
                </div>
              </div> 
            )}
          </div>
        </div>
      )}

      {/* ==================== DESKTOP EXPERIENCE ==================== */}
      {isDesktop && (
        <div className="max-w-[1120px] mx-auto px-8 pb-16 pt-8">
          <div className="mt-8 grid grid-cols-12">
            {/* LEFT: Step-based wizard or collapsed summary */}
            <div className="col-span-5">
              <div className="sticky top-8 space-y-4">{!hasCalculated || !isInputCollapsed ? <DesktopStepWizard contentSourceId={contentSourceId} salaryData={salaryData} onDataChange={handleSalaryChange} onCalculate={handleCalculate} resetTrigger={resetTrigger} /> : <CollapsedSummaryCard salaryData={salaryData} onEdit={() => {
                setIsInputCollapsed(false);
                setResetTrigger((prev) => prev + 1);
              }} />}</div>
            </div>

            {/* RIGHT: Active value panel → optimized result */}
            <div className="col-span-7">
              <div className="space-y-4">
                {!hasCalculated ? (
                  /* Before calculation: explain what user gets */
                  <ValuePreviewPanel />
                ) : (
                  <>
                    {/* Optimized result */}
                    <OptimizedResultCard 
                      salaryData={salaryData} 
                      marketData={marketData} 
                      isCalculating={isCalculating}
                      onAdjustData={() => {
                        setIsInputCollapsed(false);
                        setResetTrigger((prev) => prev + 1);
                      }}
                    />

                    {hasResult && (
                      <>
                        {/* Email signup */}
                        <EmailValueCard onEmailSent={handleLead} />
                      </>
                    )}
                  </>
                )}
                {isCalculating && (
                  <div className="spinnerWrapper">
                    <div className="spinner">
                        <img className="spinnerImg" src="/assets/images/loading.svg" alt="loader" />
                    </div>
                  </div> 
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
